import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FuelTracker from "../components/FuelTracker";
import ResidualBreakdown from "../components/ResidualBreakdown";
import SovereignAnchor from "../components/SovereignAnchor";
import SovereignShield from "../components/SovereignShield";
import GhostBridge from "../components/GhostBridge";
import SovereignEye from "../components/SovereignEye";
import { isLoopTripped, resetAuthBounces } from "../lib/authLoopBreaker";
import {
    Anchor,
    Compass,
    Crown,
    Flag,
    Globe2,
    Map as MapIcon,
    Radio,
    Rocket,
    Send,
    ShieldCheck,
    TrendingUp,
    UserSearch,
    ArrowUpRight,
    AlertTriangle,
    DollarSign,
    Users,
    FileCheck2,
    RefreshCw,
    Activity,
    Music2,
    Mic2,
    Star,
} from "lucide-react";

// Commander rank allowlist · Director directive 2026.02 (Strike #16).
// Mirrors the DIRECTOR_EMAILS pattern in App.js so the bridge can elevate
// trusted operators (e.g. Cdr. Shelia Chapple) without a backend role
// migration. Lowercase comparison.
const COMMANDER_EMAILS = new Set([
    // Cdr. Shelia Chapple · Vocal Command flagship operator
    // (Director directive 2026.02 · Strike #16 activation).
    "sheilachapple@yahoo.com",
]);

function isCommander(user) {
    if (!user) return false;
    if (user.role === "commander") return true;
    if (user.role === "director") return true; // Directors see Commander's Suite too.
    return COMMANDER_EMAILS.has((user.email || "").toLowerCase());
}

// Resolve tenant URLs: real subdomains in prod, ?tenant= override in preview
function tenantHref(tenant) {
    if (typeof window === "undefined") return `/?tenant=${tenant}`;
    const host = window.location.hostname;
    if (host === "bizboogie.com" || host.endsWith(".bizboogie.com")) {
        return `https://${tenant}.bizboogie.com`;
    }
    return `${window.location.origin}/?tenant=${tenant}`;
}

const VESSEL_DEFS = (user) => [
    {
        id: "fleet",
        name: "Fleet Command",
        codename: "EIG-01",
        tagline: user?.role === "director" ? "Master control" : "Your bridge",
        icon: Crown,
        category: "Internal",
        href: user?.role === "director" ? "/app/director" : "/app/dashboard",
        external: false,
        accent: "hsl(45 85% 65%)",
    },
    {
        id: "map",
        name: "Territorial Map",
        codename: "EIG-02",
        tagline: "VVS · 50 states",
        icon: MapIcon,
        category: "Internal",
        href: "/app/map",
        external: false,
        accent: "hsl(40 70% 55%)",
    },
    {
        id: "boarding",
        name: "Boarding Deck",
        codename: "EIG-03",
        tagline: "Onboarding & covenant",
        icon: Rocket,
        category: "Internal",
        href: "/app/boarding",
        external: false,
        accent: "hsl(40 70% 55%)",
    },
    {
        id: "scout",
        name: "Scout Network",
        codename: "EIG-04",
        tagline: "Intel transmissions",
        icon: Send,
        category: "Internal",
        href: "/app/scout",
        external: false,
        accent: "hsl(153 50% 60%)",
    },
    {
        id: "trade",
        name: "Trade Desk",
        codename: "EIG-05",
        tagline: "Market & liquidity radar",
        icon: TrendingUp,
        category: "Tenant",
        href: tenantHref("trade"),
        external: true,
        accent: "hsl(40 75% 55%)",
    },
    {
        id: "sports",
        name: "Signal Bridge",
        codename: "EIG-06",
        tagline: "NBA & sports signals",
        icon: Radio,
        category: "Tenant",
        href: tenantHref("sports"),
        external: true,
        accent: "hsl(153 50% 65%)",
    },
    {
        id: "cyberops",
        name: "Tech Ops",
        codename: "EIG-07",
        tagline: "Audit & governance",
        icon: ShieldCheck,
        category: "Tenant",
        href: tenantHref("cyberops"),
        external: true,
        accent: "hsl(280 50% 65%)",
    },
    {
        id: "recruitment",
        name: "Recruitment Foundry",
        codename: "EIG-08",
        tagline: "Lone wolves · matching engine",
        icon: UserSearch,
        category: "Tenant",
        href: "https://recruitment.eigooinc.net/shipyard",
        external: true,
        accent: "hsl(25 70% 55%)",
    },
    // Strike #16 · Master Bridge Refit
    // ----------------------------------------------------------------
    {
        id: "play",
        name: "Music Enterprise",
        codename: "EIG-09",
        tagline: "Experience Hub · play.eigooinc.net",
        icon: Music2,
        category: "Tenant",
        href: "https://play.eigooinc.net",
        external: true,
        accent: "hsl(310 70% 60%)",
    },
    {
        id: "mssheila",
        name: "MsSheila Vocal Command",
        codename: "EIG-10",
        tagline: "Flagship platform · Cdr. Shelia Chapple",
        icon: Mic2,
        category: "Tenant",
        href: "https://laymarie-fleet-board.preview.emergentagent.com",
        external: true,
        accent: "hsl(195 80% 60%)",
    },
];

const TYPE_ICON = {
    boarding: DollarSign,
    strike: AlertTriangle,
    scout_submitted: Send,
    scout_approved: FileCheck2,
    waitlist: Users,
    referral: Users,
};

const TYPE_COLOR = {
    boarding: "hsl(45 85% 68%)",
    strike: "hsl(350 75% 70%)",
    scout_submitted: "hsl(153 50% 65%)",
    scout_approved: "hsl(45 85% 68%)",
    waitlist: "hsl(280 50% 70%)",
    referral: "hsl(40 70% 60%)",
};

function timeAgo(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

export default function Bridge() {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Sovereign Privacy Perimeter (Director directive 2026.02):
    // Unauthenticated visitors NEVER see real fleet data. They get the
    // GhostBridge — a faithful template with mock values, prominent CTAs,
    // and explicit "TEMPLATE" tagging. The hooks below only fire once
    // authenticated, so /bridge/signals is never called by anonymous users.
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const vessels = useMemo(() => VESSEL_DEFS(user), [user]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/bridge/signals?limit=40");
            setSignals(data);
        } catch (_) {
            setSignals([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // Privacy Perimeter: only fetch real fleet data when authenticated.
        // Anonymous visitors short-circuit to GhostBridge below.
        if (!user) return;
        load();
        const interval = setInterval(load, 30000);
        return () => clearInterval(interval);
    }, [load, user]);

    const counts = signals.reduce((acc, s) => {
        acc[s.vessel] = (acc[s.vessel] || 0) + 1;
        return acc;
    }, {});

    const onBoard = (vessel) => {
        if (vessel.external) {
            window.open(vessel.href, "_blank", "noopener,noreferrer");
        } else {
            navigate(vessel.href);
        }
    };

    // Privacy Perimeter — render the Ghost view for anonymous visitors.
    // user === null → still loading auth state (show nothing to avoid flicker)
    // user === false → confirmed anonymous → GhostBridge with mock data + CTAs
    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground" data-testid="bridge-loading">
                Awaiting clearance...
            </div>
        );
    }
    if (!user) {
        // Auth Loop-Breaker (Strike #17 extension): if the user has been
        // bounced ≥3× in the last 30s, /bridge is part of the loop too —
        // intercept the GhostBridge render and surface the Sovereign
        // Diagnostic page instead so they can see the raw cookie state.
        // We do NOT record a bounce here (anonymous Bridge visits are
        // legitimate and shouldn't trip the breaker on their own), but we
        // DO honor an already-tripped breaker.
        if (isLoopTripped()) {
            return <Navigate to="/diagnostic" replace />;
        }
        return <GhostBridge />;
    }
    // Authenticated render reached on /bridge — wipe the tally.
    resetAuthBounces();

    return (
        <div className="min-h-screen bg-background grain-overlay">
            {/* Top bar */}
            <header className="sticky top-0 z-20 backdrop-blur-xl bg-[hsl(151_55%_8%/0.78)] border-b border-[hsl(40_40%_30%/0.18)]">
                <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
                    <div className="flex items-baseline gap-3">
                        <Link to="/" className="flex items-center gap-3" data-testid="bridge-logo">
                            <SovereignAnchor size={28} strokeWidth={6} className="text-[hsl(45_85%_68%)]" />
                            <span className="font-heading text-2xl gold-gradient-text">EIGOO</span>
                        </Link>
                        <span className="text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)]">Master Bridge</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground hidden md:inline">
                            Signed in as <span className="text-foreground">{user?.name || user?.email}</span>
                        </span>
                        <button
                            onClick={() => { setRefreshing(true); load(); }}
                            data-testid="bridge-refresh"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[hsl(40_50%_35%/0.4)] hover:bg-[hsl(151_55%_15%)] transition-colors"
                        >
                            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
                            {refreshing ? "Pinging..." : "Refresh"}
                        </button>
                    </div>
                </div>
            </header>

            <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
                {/* Sovereign Shield · Security posture HUD (Director directive 2026.02) */}
                <SovereignShield />

                <div className="fade-up max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(40_50%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(45_60%_70%)] mb-5">
                        <Compass size={11} className="text-[hsl(45_85%_62%)]" /> One place. Every vessel.
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                        The Master
                        <span className="gold-gradient-text"> Command Bridge.</span>
                    </h1>
                    <p className="mt-5 text-base text-muted-foreground max-w-xl leading-relaxed">
                        Ten sites. One sign-on. Every signal across the fleet, threaded into a single feed.
                    </p>
                </div>

                {/* Commander's Suite · elevated for COMMANDER rank operators
                    (Cdr. Shelia Chapple flagship). Surfaces the two music
                    hulls prominently so they're never buried in the radar
                    grid. Directors see this panel too. */}
                {isCommander(user) && (
                    <CommanderSuite user={user} />
                )}

                {/* Sovereign Eye · internal fleet view (Director directive 2026.02) */}
                <div className="mt-12">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <div className="font-heading text-2xl">Sovereign Eye</div>
                            <div className="text-xs text-muted-foreground">
                                Platforms (gold) · B2B leads (red) · Active deliveries (cyan) · Anchored at Honolulu
                            </div>
                        </div>
                    </div>
                    <SovereignEye mode="internal" height={560} />
                </div>

                {/* Vessel radar grid */}
                <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <div className="flex items-baseline justify-between mb-4">
                            <div>
                                <div className="font-heading text-2xl">Vessel radar</div>
                                <div className="text-xs text-muted-foreground">10 hulls under the EIGOO covenant · live status</div>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[hsl(45_85%_68%)]">
                                <span className="w-2 h-2 rounded-full bg-[hsl(45_85%_62%)] animate-pulse" />
                                all online
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="vessel-radar">
                            {vessels.map((v, idx) => {
                                const Icon = v.icon;
                                const alerts = counts[v.id] || 0;
                                return (
                                    <article
                                        key={v.id}
                                        className="glass rounded-2xl p-5 relative overflow-hidden group cursor-pointer hover:-translate-y-0.5 transition-transform fade-up"
                                        style={{ animationDelay: `${idx * 0.04}s` }}
                                        onClick={() => onBoard(v)}
                                        data-testid={`vessel-card-${v.id}`}
                                    >
                                        <div
                                            className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity"
                                            style={{ background: v.accent.replace(")", " / 0.18)") }}
                                        />
                                        <div className="relative">
                                            <div className="flex items-start justify-between">
                                                <div
                                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                                    style={{ background: v.accent.replace(")", " / 0.18)"), color: v.accent }}
                                                >
                                                    <Icon size={18} strokeWidth={1.6} />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {alerts > 0 && (
                                                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(40_61%_25%/0.6)] text-[hsl(45_85%_75%)]" data-testid={`vessel-alerts-${v.id}`}>
                                                            {alerts} signal{alerts !== 1 ? "s" : ""}
                                                        </span>
                                                    )}
                                                    <span className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground font-mono">{v.codename}</span>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <div className="font-heading text-xl tracking-tight">{v.name}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5">{v.tagline}</div>
                                            </div>
                                            <div className="mt-5 pt-4 border-t border-[hsl(40_40%_30%/0.15)] flex items-center justify-between">
                                                <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{v.category}</span>
                                                <span
                                                    className="text-xs font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all"
                                                    style={{ color: v.accent }}
                                                >
                                                    Board site
                                                    {v.external ? <ArrowUpRight size={14} /> : <Anchor size={12} />}
                                                </span>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="mt-6 p-4 rounded-lg border border-dashed border-[hsl(40_40%_30%/0.25)] text-[11px] text-muted-foreground leading-relaxed">
                            <span className="text-[hsl(45_85%_68%)] font-semibold">SSO note:</span> tenant-domain auto-auth is gated by the <code className="text-[hsl(45_85%_70%)] font-mono">COOKIE_DOMAIN</code> env var. Set <code className="text-[hsl(45_85%_70%)] font-mono">COOKIE_DOMAIN=.eigooinc.net</code> in production env to share your session across <code className="text-[hsl(45_85%_70%)] font-mono">trade./sports./cyberops./recruitment.</code>. In preview the domain is a Sovereign-managed host so this is no-op (browsers reject cross-host domain cookies).
                        </div>
                    </div>

                    {/* Global signal feed */}
                    <div className="lg:col-span-1">
                        <div className="flex items-baseline justify-between mb-4">
                            <div>
                                <div className="font-heading text-2xl">Global signal feed</div>
                                <div className="text-xs text-muted-foreground">Aggregated across all vessels · 30s refresh</div>
                            </div>
                            <Activity size={14} className="text-[hsl(45_85%_68%)]" />
                        </div>

                        <div className="glass rounded-2xl p-4 max-h-[760px] overflow-y-auto" data-testid="signal-feed">
                            {loading ? (
                                <div className="text-sm text-muted-foreground py-8 text-center">Reading the wires...</div>
                            ) : signals.length === 0 ? (
                                <div className="text-sm text-muted-foreground py-8 text-center">No signals yet. Once boardings, strikes, scouts and waitlists start firing, they&apos;ll thread in here.</div>
                            ) : (
                                <ul className="space-y-2">
                                    {signals.map((s) => {
                                        const Icon = TYPE_ICON[s.type] || Flag;
                                        const color = TYPE_COLOR[s.type] || "hsl(45 85% 68%)";
                                        return (
                                            <li
                                                key={s.id}
                                                className="p-3 rounded-lg bg-[hsl(151_55%_13%)] border border-[hsl(40_40%_25%/0.16)] flex items-start gap-3"
                                                data-testid={`signal-${s.id}`}
                                            >
                                                <div
                                                    className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                                                    style={{ background: color.replace(")", " / 0.18)"), color }}
                                                >
                                                    <Icon size={14} strokeWidth={1.6} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm leading-tight truncate">{s.title}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{s.body}</div>
                                                </div>
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                                                    {timeAgo(s.timestamp)}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-12 text-center">
                    <Link to="/" className="text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
                        <Globe2 size={12} /> Return to Hub
                    </Link>
                </div>

                {user?.role === "director" && <FuelTracker />}
                {user?.role === "director" && <ResidualBreakdown />}
            </section>
        </div>
    );
}

/**
 * CommanderSuite · Director directive 2026.02 (Strike #16)
 *
 * Pinned hero panel rendered above the Vessel Radar for users with
 * COMMANDER rank. Surfaces the two flagship music-fleet hulls so the
 * Commander never has to dig through the 10-card grid to find her
 * own ship.
 *
 * Visible to:
 *   - user.role === "commander"
 *   - user.email in COMMANDER_EMAILS (frontend allowlist)
 *   - user.role === "director" (Director always sees Commander views)
 */
function CommanderSuite({ user }) {
    const first = (user?.name || user?.email || "Commander").split(/[\s@]/)[0];
    const SUITE_VESSELS = [
        {
            id: "play",
            name: "Music Enterprise",
            sub: "Experience Hub · play.eigooinc.net",
            icon: Music2,
            href: "https://play.eigooinc.net",
            accent: "#E8C56C",
        },
        {
            id: "mssheila",
            name: "MsSheila Vocal Command",
            sub: "Flagship platform · podcast + studio",
            icon: Mic2,
            href: "https://laymarie-fleet-board.preview.emergentagent.com",
            accent: "#00E5FF",
        },
    ];

    return (
        <section
            data-testid="commander-suite"
            className="mt-10 relative overflow-hidden"
            style={{
                background: "linear-gradient(135deg, #0D1824 0%, #132337 100%)",
                border: "1px solid #E8C56C",
                boxShadow: "0 0 40px rgba(232,197,108,0.18)",
            }}
        >
            <span aria-hidden className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: "#E8C56C" }} />
            <span aria-hidden className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: "#E8C56C" }} />
            <span aria-hidden className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: "#E8C56C" }} />
            <span aria-hidden className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: "#E8C56C" }} />

            <div className="px-6 md:px-10 py-8">
                <div
                    className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] mb-3"
                    style={{
                        color: "#E8C56C",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                >
                    <Star size={14} />
                    Commander&rsquo;s Suite
                    <span
                        className="ml-2 px-2 py-0.5 text-[9px]"
                        style={{
                            background: "rgba(232,197,108,0.12)",
                            border: "1px solid #E8C56C55",
                            color: "#E8C56C",
                        }}
                        data-testid="commander-rank-badge"
                    >
                        COMMANDER
                    </span>
                </div>
                <h2
                    data-testid="commander-greeting"
                    className="text-2xl md:text-3xl uppercase font-black leading-tight tracking-tight"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                >
                    Welcome aboard, Commander {first}.
                </h2>
                <p
                    className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed"
                    style={{ color: "#A9BCD2" }}
                >
                    Your flagship platforms are live and patched into the Sovereign
                    grid. Direct visibility on the Experience Hub and your own
                    Vocal Command ship — no waypoint hopping required.
                </p>

                <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {SUITE_VESSELS.map((v) => {
                        const Icon = v.icon;
                        return (
                            <a
                                key={v.id}
                                href={v.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`commander-vessel-${v.id}`}
                                className="group relative flex items-center gap-4 p-5 transition-all duration-300"
                                style={{
                                    background: "rgba(5,10,15,0.55)",
                                    border: `1px solid ${v.accent}44`,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = v.accent;
                                    e.currentTarget.style.boxShadow = `0 0 25px ${v.accent}55`;
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = `${v.accent}44`;
                                    e.currentTarget.style.boxShadow = "none";
                                    e.currentTarget.style.transform = "translateY(0)";
                                }}
                            >
                                <div
                                    className="w-12 h-12 flex items-center justify-center shrink-0"
                                    style={{
                                        background: `${v.accent}14`,
                                        border: `1px solid ${v.accent}55`,
                                        color: v.accent,
                                    }}
                                >
                                    <Icon size={20} strokeWidth={1.6} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div
                                        className="text-base md:text-lg uppercase font-bold tracking-tight"
                                        style={{ fontFamily: "'Montserrat', sans-serif", color: "#F8FAFC" }}
                                    >
                                        {v.name}
                                    </div>
                                    <div className="text-xs mt-0.5" style={{ color: "#829AB1" }}>
                                        {v.sub}
                                    </div>
                                </div>
                                <ArrowUpRight
                                    size={18}
                                    className="shrink-0 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                                    style={{ color: v.accent }}
                                />
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
